import type { QueryClient } from "@tanstack/react-query";
import { createStore, useStore, type StoreApi } from "zustand";

export type SettingsDesiredMutation = Readonly<
  | { kind: "commission"; rate: number }
  | { kind: "inquiry-limit"; limit: number }
  | { kind: "campaign-fees"; fees: Readonly<Record<string, string>> }
  | { desiredValue: boolean; key: string; kind: "feature-flag" }
  | { desiredValue: boolean; kind: "maintenance" }
>;

export type SettingsAuthorityResolution = Readonly<{
  desired: SettingsDesiredMutation;
  outcome: "confirmed" | "diverged";
  sequence: number;
}>;

export type SettingsWriteLease = Readonly<{
  generation: number;
  id: number;
}>;

interface SettingsWriteFence {
  generation: number;
  nextId: number;
  pending: SettingsWriteLease | null;
}

interface SettingsAuthorityState {
  recovery: SettingsDesiredMutation | null;
  refreshing: boolean;
  resolution: SettingsAuthorityResolution | null;
  resolutionSequence: number;
  writing: boolean;
  beginWriteOperation: () => SettingsWriteLease | null;
  clearResolution: (expected: SettingsAuthorityResolution) => boolean;
  finishRefresh: (expected: SettingsDesiredMutation) => void;
  finishWrite: (lease?: SettingsWriteLease) => void;
  isWriteOperationCurrent: (lease: SettingsWriteLease) => boolean;
  recordRecovery: (desired: SettingsDesiredMutation) => void;
  recordResolution: (
    desired: SettingsDesiredMutation,
    outcome: SettingsAuthorityResolution["outcome"],
  ) => void;
  resolveRecovery: (
    expected: SettingsDesiredMutation,
    outcome: SettingsAuthorityResolution["outcome"],
  ) => boolean;
  reset: () => void;
  tryStartRefresh: (expected: SettingsDesiredMutation) => boolean;
  tryStartWrite: () => boolean;
}

export type SettingsAuthorityStore = StoreApi<SettingsAuthorityState>;

const storesByQueryClient = new WeakMap<QueryClient, SettingsAuthorityStore>();
const fencesByQueryClient = new WeakMap<QueryClient, SettingsWriteFence>();

function writeFenceFor(queryClient: QueryClient): SettingsWriteFence {
  const existing = fencesByQueryClient.get(queryClient);
  if (existing) return existing;
  const fence: SettingsWriteFence = {
    generation: 0,
    nextId: 0,
    pending: null,
  };
  fencesByQueryClient.set(queryClient, fence);
  return fence;
}

function nextResolution(
  state: SettingsAuthorityState,
  desired: SettingsDesiredMutation,
  outcome: SettingsAuthorityResolution["outcome"],
): SettingsAuthorityResolution {
  return Object.freeze({
    desired,
    outcome,
    sequence: state.resolutionSequence + 1,
  });
}

function createSettingsAuthorityStore(
  queryClient: QueryClient,
  fence: SettingsWriteFence,
): SettingsAuthorityStore {
  return createStore<SettingsAuthorityState>((set) => ({
    recovery: null,
    refreshing: false,
    resolution: null,
    resolutionSequence: 0,
    writing: fence.pending !== null,
    beginWriteOperation: () => {
      let lease: SettingsWriteLease | null = null;
      set((state) => {
        if (!state.writing || fence.pending !== null) return state;
        lease = Object.freeze({
          generation: fence.generation,
          id: ++fence.nextId,
        });
        fence.pending = lease;
        return state;
      });
      return lease;
    },
    clearResolution: (expected) => {
      let cleared = false;
      set((state) => {
        if (state.resolution !== expected) return state;
        cleared = true;
        return { resolution: null };
      });
      return cleared;
    },
    finishRefresh: (expected) =>
      set((state) =>
        state.recovery === expected && state.refreshing
          ? { refreshing: false }
          : state,
      ),
    finishWrite: (lease) => {
      if (lease && fence.pending === lease) {
        fence.pending = null;
      }
      const writing = fence.pending !== null;
      set({ writing });
      storesByQueryClient.get(queryClient)?.setState({ writing });
    },
    isWriteOperationCurrent: (lease) =>
      fence.pending === lease && lease.generation === fence.generation,
    recordRecovery: (recovery) =>
      set({ recovery, refreshing: false, resolution: null }),
    recordResolution: (desired, outcome) =>
      set((state) => {
        const resolution = nextResolution(state, desired, outcome);
        return {
          recovery: null,
          refreshing: false,
          resolution,
          resolutionSequence: resolution.sequence,
        };
      }),
    resolveRecovery: (expected, outcome) => {
      let resolved = false;
      set((state) => {
        if (state.recovery !== expected) return state;
        resolved = true;
        const resolution = nextResolution(state, expected, outcome);
        return {
          recovery: null,
          refreshing: false,
          resolution,
          resolutionSequence: resolution.sequence,
        };
      });
      return resolved;
    },
    reset: () =>
      set({
        recovery: null,
        refreshing: false,
        resolution: null,
        resolutionSequence: 0,
        writing: false,
      }),
    tryStartRefresh: (expected) => {
      let started = false;
      set((state) => {
        if (state.recovery !== expected || state.refreshing || state.writing) {
          return state;
        }
        started = true;
        return { refreshing: true };
      });
      return started;
    },
    tryStartWrite: () => {
      let started = false;
      set((state) => {
        if (state.recovery || state.refreshing || state.writing) return state;
        started = true;
        return { resolution: null, writing: true };
      });
      return started;
    },
  }));
}

export function settingsAuthorityStoreFor(
  queryClient: QueryClient,
): SettingsAuthorityStore {
  const existing = storesByQueryClient.get(queryClient);
  if (existing) return existing;
  const store = createSettingsAuthorityStore(
    queryClient,
    writeFenceFor(queryClient),
  );
  storesByQueryClient.set(queryClient, store);
  return store;
}

export function useSettingsAuthority(queryClient: QueryClient) {
  return useStore(settingsAuthorityStoreFor(queryClient));
}

export function resetSettingsAuthority(queryClient: QueryClient): void {
  writeFenceFor(queryClient).generation += 1;
  storesByQueryClient.get(queryClient)?.getState().reset();
  storesByQueryClient.delete(queryClient);
}
